import type { JournalEntryDraft, JournalPrivacy, JournalPrompts } from '../../types/auth';

/**
 * CORE-1455 — что показать после практики и в каком порядке.
 *
 * Логика отделена от экрана намеренно: решение «показывать ли окно приватности»
 * зависит от трёх состояний, и перепутать их легко. Проверяемая функция стоит
 * дешевле, чем разбирательство в компоненте.
 */

/** Что открывается после практики. */
export type ReflectionStep =
  /** Человек ещё не выбирал уровень приватности — объясняем один раз. */
  | 'privacy'
  /** Выбор сделан, показываем сами вопросы. */
  | 'questions'
  /** Показывать нечего: рефлексия выключена или это гость. */
  | 'none';

export interface ReflectionGateInput {
  /** Авторизован ли человек — гостю дневник не предлагаем. */
  signedIn: boolean;
  /** Настройка «Агапелла открывает рефлексию после практики». */
  reflectionEnabled: boolean;
  /** Ответ сервера; `undefined` — ещё не загрузили. */
  privacy?: JournalPrivacy;
}

/**
 * Решение о первом шаге.
 *
 * Третье состояние приватности — `null`, «ещё не выбирал» — и есть причина, по
 * которой эта функция существует. Спутать его с `false` значит показывать окно
 * с объяснением отказавшемуся при каждой рефлексии, то есть ровно то, от чего
 * уходили: Надежда просила «чтобы каждый раз при новой рефлексии не указывать
 * настройки конфиденциальности».
 *
 * Пока ответ не загружен — не показываем ничего. Мигнуть окном приватности и
 * убрать его хуже, чем открыться на полсекунды позже.
 */
export function resolveReflectionStep(input: ReflectionGateInput): ReflectionStep {
  if (!input.signedIn) return 'none';
  if (!input.reflectionEnabled) return 'none';
  if (!input.privacy) return 'none';
  return input.privacy.aiMayRead === null ? 'privacy' : 'questions';
}

/**
 * Есть ли в черновике хоть одно слово.
 *
 * Пустую запись сервер не сохраняет и возвращает пусто — проверять здесь нужно,
 * чтобы не отправлять заведомо бессмысленный запрос и не показывать человеку
 * «сохранено» там, где ничего не сохранилось.
 */
export function hasContent(draft: JournalEntryDraft): boolean {
  return [
    draft.before, draft.feeling, draft.insights,
    draft.takeAway, draft.gratitude, draft.freeForm,
  ].some((value) => typeof value === 'string' && value.trim().length > 0);
}

/**
 * Черновик без пустых полей — то, что уходит на сервер.
 *
 * Пустые строки убираются, потому что «поле прислали пустым» и «поле не
 * трогали» на сервере различаются: первое стирает прежний текст при правке.
 */
export function cleanDraft(draft: JournalEntryDraft): JournalEntryDraft {
  const out: JournalEntryDraft = {};
  if (draft.practiceCompletionId != null) out.practiceCompletionId = draft.practiceCompletionId;
  (['before', 'feeling', 'insights', 'takeAway', 'gratitude', 'freeForm'] as const)
    .forEach((key) => {
      const value = draft[key];
      if (typeof value === 'string' && value.trim().length > 0) out[key] = value.trim();
    });
  return out;
}

/**
 * Порядок полей на экране — тот, в котором вопросы пришли с сервера.
 *
 * Клиент свой порядок не назначает: список правится продуктом на сервере, и
 * второй порядок здесь разошёлся бы с ним молча.
 */
export function orderedFields(prompts: JournalPrompts | undefined): string[] {
  return prompts?.prompts.map((p) => p.field) ?? [];
}
